import React from 'react';
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaBars, FaTimes, FaSearch, FaUser } from 'react-icons/fa';
import { Logo } from './Logo';
import { useLanguage } from '../context/LanguageContext';
import BrandText from '../styles/brandStyles';

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const { translate } = useLanguage();
  const navigate = useNavigate();
  
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
    if (searchOpen) setSearchOpen(false);
  };
  
  const toggleSearch = () => {
    setSearchOpen(!searchOpen);
    if (mobileMenuOpen) setMobileMenuOpen(false);
  };
  
  // Función para cerrar el menú móvil y hacer scroll al inicio al navegar
  const handleNavClick = (to) => {
    // Cerrar el menú móvil
    if (mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
    
    // Hacer scroll al inicio después de la navegación
    setTimeout(() => {
      window.scrollTo(0, 0);
      
      // Métodos alternativos para asegurar el scroll
      if (document.body) {
        document.body.scrollTop = 0;
      }
      
      if (document.documentElement) {
        document.documentElement.scrollTop = 0;
      }
    }, 0);
  };
  
  return (
    <header className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-[#003171] shadow-md py-2' : 'bg-[#003171]/95 py-3'}`}>
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <Link to="/" onClick={() => handleNavClick()}>
            <Logo isWhite={true} />
          </Link>
          
          {/* Desktop Navigation */}
          <nav className="hidden lg:flex space-x-6 ml-8">
            <div className="group relative">
              <button className="px-1 py-2 text-gray-200 text-base uppercase font-medium hover:text-[#0066CC] flex items-center">
                {translate('products')} <span className="ml-1">▼</span>
              </button>
              <div className="absolute left-0 mt-2 w-[800px] rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300">
                <div className="grid grid-cols-3 gap-4 p-6">
                  {/* OPUS Column */}
                  <div className="space-y-4">
                    <h3 className="font-bold text-lg text-[#0066CC] mb-2">
                      <BrandText>OPUS</BrandText>
                    </h3>
                    <div className="bg-gray-50 p-3 rounded-lg mb-4 h-24 flex items-center justify-center">
                      <div className="flex flex-col items-center justify-center space-y-2">
                        <img src="/images/opus-logo.svg" alt="OPUS Logo" className="w-24 h-auto" />
                        <p className="text-xs text-gray-600 text-center">Software especializado en gestión de proyectos de construcción</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Link to="/modulos/presupuesto-programable" className="block text-gray-800 hover:text-[#0066CC]" onClick={() => handleNavClick()}>
                        <span className="font-semibold">M1</span> - Presupuesto Programable
                      </Link>
                      <Link to="/modulos/planeacion-control" className="block text-gray-800 hover:text-[#0066CC]" onClick={() => handleNavClick()}>
                        <span className="font-semibold">M2</span> - Planeación y Control
                      </Link>
                      <Link to="/modulos/administracion-compras" className="block text-gray-800 hover:text-[#0066CC]" onClick={() => handleNavClick()}>
                        <span className="font-semibold">M3</span> - Administración de Compras
                      </Link>
                      <Link to="/modulos/dependencias" className="block text-gray-800 hover:text-[#0066CC]" onClick={() => handleNavClick()}>
                        <span className="font-semibold">M4</span> - Dependencias
                      </Link>
                      <Link to="/modulos/cuantificacion-bim" className="block text-gray-800 hover:text-[#0066CC]" onClick={() => handleNavClick()}>
                        <span className="font-semibold">M5</span> - Cuantificación BIM
                      </Link>
                    </div>
                  </div>

                  {/* INFORTE Column */}
                  <div className="space-y-4">
                    <h3 className="font-bold text-lg text-[#0066CC] mb-2">
                      <BrandText>INFORTE</BrandText>
                    </h3>
                    <div className="bg-gray-50 p-3 rounded-lg mb-4 h-24 flex items-center justify-center">
                      <div className="flex flex-col items-center justify-center space-y-2">
                        <img src="/images/inforte-logo.svg" alt="INFORTE Logo" className="w-24 h-auto" />
                        <p className="text-xs text-gray-600 text-center">Servicio de información de proyectos y obras</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Link to="/inforte" className="block text-gray-800 hover:text-[#0066CC] font-semibold" onClick={() => handleNavClick()}>
                        Ver INFORTE
                      </Link>
                      <a href="https://ecostos.com.mx/" target="_blank" rel="noopener noreferrer" className="block text-gray-800 hover:text-[#0066CC] font-semibold">
                        Ver ECOSTOS
                      </a>
                    </div>
                  </div>

                  {/* OPUS ERP Column */}
                  <div className="space-y-4">
                    <h3 className="font-bold text-lg text-[#0066CC] mb-2">
                      <BrandText>OPUS ERP</BrandText>
                    </h3>
                    <div className="bg-gray-50 p-3 rounded-lg mb-4 h-24 flex items-center justify-center">
                      <div className="flex flex-col items-center justify-center space-y-2">
                        <img src="/images/opus-erp-logo.svg" alt="OPUS ERP Logo" className="w-24 h-auto" />
                        <p className="text-xs text-gray-600 text-center">Sistema integral de gestión empresarial</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Link to="/erp/operaciones" className="block text-gray-800 hover:text-[#0066CC]" onClick={() => handleNavClick()}>
                        Operaciones
                      </Link>
                      <Link to="/erp/ventas" className="block text-gray-800 hover:text-[#0066CC]" onClick={() => handleNavClick()}>
                        Ventas
                      </Link>
                      <Link to="/erp/productividad" className="block text-gray-800 hover:text-[#0066CC]" onClick={() => handleNavClick()}>
                        Productividad
                      </Link>
                      <Link to="/erp/web" className="block text-gray-800 hover:text-[#0066CC]" onClick={() => handleNavClick()}>
                        Web
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="group relative">
              <button className="px-1 py-2 text-gray-200 text-base uppercase font-medium hover:text-[#0066CC] flex items-center">
                {translate('support')} <span className="ml-1">▼</span>
              </button>
              <div className="absolute left-0 mt-2 w-80 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300">
                <div className="py-1">
                  <Link to="/contacto" className="block px-4 py-2 text-gray-800 hover:bg-gray-100" onClick={() => handleNavClick()}>
                    {translate('support_request')}
                  </Link>
                  <Link to="/soporte/faq" className="block px-4 py-2 text-gray-800 hover:bg-gray-100" onClick={() => handleNavClick()}>
                    {translate('faq')}
                  </Link>
                  <Link to="/soporte/RepararServidorDeCarpeta" className="block px-4 py-2 text-gray-800 hover:bg-gray-100" onClick={() => handleNavClick()}>
                    REPARAR SERVIDOR CARPETA
                  </Link>
                  <Link to="/soporte/anywhere" className="block px-4 py-2 text-gray-800 hover:bg-gray-100" onClick={() => handleNavClick()}>
                    CONECTIVIDAD ANYWHERE
                  </Link>
                </div>
              </div>
            </div>
            
            <Link to="/soporte/descargas" className="px-1 py-2 text-gray-200 text-base uppercase font-medium hover:text-[#0066CC]" onClick={() => handleNavClick()}>
              {translate('downloads')}
            </Link>
            
            <Link to="/academia-opus" className="px-1 py-2 text-gray-200 text-base uppercase font-medium hover:text-[#0066CC]" onClick={() => handleNavClick()}>
              ACADEMIA OPUS
            </Link>
            
            <Link to="/distribuidores" className="px-1 py-2 text-gray-200 text-base uppercase font-medium hover:text-[#0066CC]" onClick={() => handleNavClick()}>
              {translate('buy')}
            </Link>
          </nav>
          
          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center space-x-6">
            <button 
              onClick={toggleSearch}
              className="text-gray-200 hover:text-[#0066CC]"
              aria-label={translate('search')}
            >
              <FaSearch size={18} />
            </button>
            <Link to="/cuenta" className="text-gray-200 hover:text-[#0066CC]" onClick={() => handleNavClick()}>
              <FaUser size={18} />
            </Link>
          </div>
          
          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center">
            <button
              onClick={toggleMobileMenu}
              className="p-2 text-gray-200 hover:text-white"
            >
              {mobileMenuOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
            </button>
          </div>
        </div>
      </div>
      
      {/* Search Bar */}
      {searchOpen && (
        <div className="absolute top-full left-0 w-full bg-white shadow-md py-4">
          <div className="container mx-auto px-4">
            <div className="relative">
              <input 
                type="text" 
                placeholder={translate('search_placeholder')} 
                className="w-full px-4 py-2 pl-10 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#0066CC]"
              />
              <FaSearch className="absolute left-3 top-3 text-gray-400" />
              <button 
                className="absolute right-3 top-2 text-gray-500 hover:text-[#0066CC]"
                onClick={toggleSearch}
              >
                <FaTimes size={18} />
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-[#0F1A24]">
          <div className="container mx-auto px-4 py-4">
            <div className="space-y-6">
              <div>
                <h3 className="font-bold text-white mb-2 border-b border-gray-700 pb-2">{translate('products')}</h3>
                <div className="pl-4 space-y-2">
                  <h4 className="font-medium text-white mt-2"><BrandText>OPUS</BrandText></h4>
                  <ul className="pl-4 space-y-1">
                    <li><Link to="/modulos/presupuesto-programable" className="text-gray-300 hover:text-[#0066CC]" onClick={() => handleNavClick()}>
                      {translate('budget_module')}
                    </Link></li>
                    <li><Link to="/modulos/planeacion-control" className="text-gray-300 hover:text-[#0066CC]" onClick={() => handleNavClick()}>
                      {translate('planning_module')}
                    </Link></li>
                    <li><Link to="/modulos/administracion-compras" className="text-gray-300 hover:text-[#0066CC]" onClick={() => handleNavClick()}>
                      {translate('purchases_module')}
                    </Link></li>
                    <li><Link to="/modulos/dependencias" className="text-gray-300 hover:text-[#0066CC]" onClick={() => handleNavClick()}>
                      {translate('dependencies_module')}
                    </Link></li>
                    <li><Link to="/modulos/cuantificacion-bim" className="text-gray-300 hover:text-[#0066CC]" onClick={() => handleNavClick()}>
                      {translate('bim_module')}
                    </Link></li>
                    <li><Link to="/modulos/erp-enterprise" className="text-gray-300 hover:text-[#0066CC]" onClick={() => handleNavClick()}>
                      {translate('erp_module')}
                    </Link></li>
                  </ul>

                  <h4 className="font-medium text-white mt-4"><BrandText>OPUS ERP</BrandText></h4>
                  <ul className="pl-4 space-y-1">
                    <li><Link to="/erp/operaciones" className="text-gray-300 hover:text-[#0066CC]" onClick={() => handleNavClick()}>
                      Operaciones
                    </Link></li>
                    <li><Link to="/erp/ventas" className="text-gray-300 hover:text-[#0066CC]" onClick={() => handleNavClick()}>
                      Ventas
                    </Link></li>
                    <li><Link to="/erp/productividad" className="text-gray-300 hover:text-[#0066CC]" onClick={() => handleNavClick()}>
                      Productividad
                    </Link></li>
                    <li><Link to="/erp/web" className="text-gray-300 hover:text-[#0066CC]" onClick={() => handleNavClick()}>
                      Web
                    </Link></li>
                  </ul>
                  
                  <h4 className="font-medium text-white mt-4"><BrandText>INFORTE</BrandText></h4>
                  <ul className="pl-4 space-y-1">
                    <li><Link to="/inforte" className="text-gray-300 hover:text-[#0066CC]" onClick={() => handleNavClick()}>
                      Información de Proyectos
                    </Link></li>
                    <li><a href="https://ecostos.com.mx/" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-[#0066CC]">
                      ECOSTOS - Consejos de Construcción
                    </a></li>
                  </ul>
                </div>
              </div>
              
              <div>
                <h3 className="font-bold text-white mb-2 border-b border-gray-700 pb-2">{translate('support')}</h3>
                <ul className="pl-4 space-y-2">
                  <li><Link to="/contacto" className="text-gray-300 hover:text-[#0066CC]" onClick={() => handleNavClick()}>{translate('support_request')}</Link></li>
                  <li><Link to="/soporte/faq" className="text-gray-300 hover:text-[#0066CC]" onClick={() => handleNavClick()}>{translate('faq')}</Link></li>
                  <li><Link to="/soporte/RepararServidorDeCarpeta" className="text-gray-300 hover:text-[#0066CC]" onClick={() => handleNavClick()}>REPARAR SERVIDOR CARPETA</Link></li>
                  <li><Link to="/soporte/anywhere" className="text-gray-300 hover:text-[#0066CC]" onClick={() => handleNavClick()}>CONECTIVIDAD ANYWHERE</Link></li>
                </ul>
              </div>
              
              <div>
                <h3 className="font-bold text-white mb-2 border-b border-gray-700 pb-2">{translate('downloads')}</h3>
                <div className="pl-4">
                  <Link to="/soporte/descargas" className="text-gray-300 hover:text-[#0066CC]" onClick={() => handleNavClick()}>{translate('downloads')}</Link>
                </div>
              </div>
              
              <div>
                <h3 className="font-bold text-white mb-2 border-b border-gray-700 pb-2">ACADEMIA OPUS</h3>
                <div className="pl-4">
                  <Link to="/academia-opus" className="text-gray-300 hover:text-[#0066CC]" onClick={() => handleNavClick()}>
                    Ver Academia
                  </Link>
                </div>
              </div>
              
              <div>
                <h3 className="font-bold text-white mb-2 border-b border-gray-700 pb-2">{translate('buy')}</h3>
                <div className="pl-4">
                  <Link to="/distribuidores" className="text-gray-300 hover:text-[#0066CC]" onClick={() => handleNavClick()}>{translate('see_purchase_options')}</Link>
                </div>
              </div>
              
              <div>
                <h3 className="font-bold text-white mb-2 border-b border-gray-700 pb-2">Contacto</h3>
                <div className="pl-4">
                  <Link to="/contacto" className="text-gray-300 hover:text-[#0066CC]" onClick={() => handleNavClick()}>
                    INFORMACIÓN DE CONTACTO
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}; 